import { Switch, Route, useLocation } from "wouter";
import { queryClient, STATIC_DATA_CONFIG } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import HomePage from "@/pages/HomePage";
import RestaurantListingPageNew from "@/pages/RestaurantListingPage"; // Neue modernere Version
import ErrorBoundary from "@/components/ErrorBoundary";
import RestaurantDetailPage from "@/pages/RestaurantDetailPage";
import RestaurantCreatePage from "@/pages/RestaurantCreatePage";
import RestaurantEditPage from "@/pages/RestaurantEditPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AuthPage from "@/pages/auth-page";
import ProfilePage from "@/pages/ProfilePage";
import ImpressumPage from "@/pages/impressum-page";
import AboutPage from "@/pages/about-page";
import ContactPage from "@/pages/contact-page";
import WieFunktioniertSpeiseGuruPage from "@/pages/WieFunktioniertSpeiseGuruPage";
import GutscheinGewinnenPage from "@/pages/GutscheinGewinnenPage";
import WarumSpeiseGuruPage from "@/pages/WarumSpeiseGuruPage";
import { useAffiliateConversionTracker } from "@/hooks/use-affiliate-tracking";
import WirGestaltenPage from "@/pages/WirGestaltenPage";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { AuthProvider } from "@/hooks/use-auth";
import { ProtectedRoute } from "@/lib/protected-route";
import { useScrollRestoration } from "@/hooks/use-scroll-restoration";
import { useEffect } from "react";
import { queryKeys } from "@/lib/queryKeys";

// Affiliate Portal Pages (eigenes Layout, kein Navbar/Footer)
import {
  AffiliateDashboardPage,
  AffiliateLeadsPage,
  AffiliateLokalePage,
  AffiliateProvisionenPage,
  AffiliateKonstantleiterPage,
  AffiliateMarketingPage,
  AffiliateZahlungenPage,
  AffiliateEinstellungenPage,
} from "@/pages/affiliate";

// After OAuth redirect: clean up ?oauth parameter from URL
// NOTE: Auth cache handling is done in AuthProvider's loadUserFromStorage()
// We must NOT remove/invalidate queries here — that would cancel the fetch
// that AuthProvider already started with initialData: undefined + staleTime: 0
function useOAuthRedirectHandler() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('oauth')) {
      // Remove ?oauth param from URL without reload (cosmetic cleanup only)
      params.delete('oauth');
      const cleanUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, []);
}

// P0.3: Layout Measurement Hook - Single Source of Truth für Sticky Offsets
function useLayoutMeasurement() {
  const [location] = useLocation();

  useEffect(() => {
    let timeoutId: number | undefined;
    let rafId: number | undefined;
    let navbarObserver: ResizeObserver | null = null;
    let toolbarObserver: ResizeObserver | null = null;
    let chipsObserver: ResizeObserver | null = null;
    let observedNavbar: Element | null = null;
    let observedToolbar: Element | null = null;
    let observedChips: Element | null = null;
    
    const updateLayoutVars = () => {
      // Debounce with requestAnimationFrame for smooth updates
      if (rafId) cancelAnimationFrame(rafId);
      if (timeoutId) clearTimeout(timeoutId);
      
      timeoutId = window.setTimeout(() => {
        rafId = requestAnimationFrame(() => {
          const root = document.documentElement;
          
          // Navbar Höhe messen
          const navbar = document.querySelector('nav');
          if (navbar) {
            const navHeight = navbar.getBoundingClientRect().height;
            root.style.setProperty('--app-nav-h', `${navHeight}px`);
          }
          
          // Toolbar Höhe messen (Desktop)
          const toolbar = document.getElementById('toolbar');
          if (toolbar) {
            const toolbarHeight = toolbar.getBoundingClientRect().height;
            root.style.setProperty('--toolbar-h', `${toolbarHeight}px`);
          } else {
            // Mobile: Toolbar ist FAB, kein sticky
            root.style.setProperty('--toolbar-h', '0px');
          }
          
          // ActiveFiltersBar Höhe messen (optional, für Sidebar offset)
          const chipsBar = document.querySelector('[class*="ActiveFiltersBar"]') as HTMLElement;
          if (chipsBar) {
            const chipsHeight = chipsBar.getBoundingClientRect().height;
            root.style.setProperty('--chips-h', `${chipsHeight}px`);
          } else {
            root.style.setProperty('--chips-h', '0px');
          }
        });
      }, 50); // 50ms debounce
    };
    
    const observeElements = () => {
      const navbar = document.querySelector('nav');
      if (navbar && navbar !== observedNavbar) {
        if (navbarObserver) navbarObserver.disconnect();
        navbarObserver = new ResizeObserver(updateLayoutVars);
        navbarObserver.observe(navbar);
        observedNavbar = navbar;
      }

      const toolbar = document.getElementById('toolbar');
      if (toolbar && toolbar !== observedToolbar) {
        if (toolbarObserver) toolbarObserver.disconnect();
        toolbarObserver = new ResizeObserver(updateLayoutVars);
        toolbarObserver.observe(toolbar);
        observedToolbar = toolbar;
      }

      const chipsBar = document.querySelector('[class*="ActiveFiltersBar"]');
      if (chipsBar && chipsBar !== observedChips) {
        if (chipsObserver) chipsObserver.disconnect();
        chipsObserver = new ResizeObserver(updateLayoutVars);
        chipsObserver.observe(chipsBar);
        observedChips = chipsBar;
      }

      updateLayoutVars();
    };

    // Initial measurement + observers
    observeElements();

    // MutationObserver for route changes / dynamic toolbars & chips
    const mutationObserver = new MutationObserver(() => {
      observeElements();
    });
    // Only watch direct children of body (not subtree) to avoid reflow on every Radix portal mount
    mutationObserver.observe(document.body, { childList: true, subtree: false });
    
    // Window resize als Fallback
    window.addEventListener('resize', updateLayoutVars, { passive: true });
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (rafId) cancelAnimationFrame(rafId);
      if (navbarObserver) navbarObserver.disconnect();
      if (toolbarObserver) toolbarObserver.disconnect();
      if (chipsObserver) chipsObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', updateLayoutVars);
    };
  }, [location]);
}

// Main site router (with Navbar/Footer)
function MainRouter() {
  return (
    <Switch>
      <Route path="/" component={HomePage} />
      {/* P0.5: ErrorBoundary für Restaurant-Liste - verhindert grauen Screen bei Mobile-Crashes */}
      <Route path="/restaurants">
        <ErrorBoundary
          onError={(error, errorInfo) => {
            console.error('RestaurantListingPageNew Error:', error, errorInfo);
            // Optional: Log to error reporting service
          }}
        >
          <RestaurantListingPageNew />
        </ErrorBoundary>
      </Route>
      <ProtectedRoute path="/restaurants/create" component={RestaurantCreatePage} />
      <ProtectedRoute path="/restaurants/edit/:id" component={RestaurantEditPage} adminOnly />
      <Route path="/restaurants/:identifier" component={RestaurantDetailPage} /> {/* Kombinierte Route für Slug oder ID */}
      {/* Admin: Route-Level Crash Protection (zusätzlich zu Tab-Level Catch im Admin) */}
      <ProtectedRoute
        path="/admin"
        component={() => (
          <ErrorBoundary
            onError={(error, errorInfo) => {
              console.error('AdminDashboard Error:', error, errorInfo);
            }}
          >
            <AdminDashboardPage />
          </ErrorBoundary>
        )}
        adminOnly
      />
      <ProtectedRoute
        path="/admin/restaurants/edit/:id"
        component={() => (
          <ErrorBoundary
            onError={(error, errorInfo) => {
              console.error('RestaurantEditPage(Admin) Error:', error, errorInfo);
            }}
          >
            <RestaurantEditPage />
          </ErrorBoundary>
        )}
        adminOnly
      />
      <Route path="/auth" component={AuthPage} />
      <ProtectedRoute path="/profil" component={ProfilePage} />
      <Route path="/impressum" component={ImpressumPage} />
      <Route path="/ueber-uns" component={AboutPage} />
      <Route path="/kontakt" component={ContactPage} />
      <Route path="/wie-funktioniert" component={WieFunktioniertSpeiseGuruPage} />
      <Route path="/gutschein-gewinnen" component={GutscheinGewinnenPage} />
      <Route path="/warum-speiseguru" component={WarumSpeiseGuruPage} />
      <Route path="/wir-gestalten" component={WirGestaltenPage} />
      <Route component={NotFound} />
    </Switch>
  );
}

// Affiliate Portal Router (eigenes Layout, kein Navbar/Footer)
function AffiliateRouter() {
  return (
    <Switch>
      <Route path="/affiliate" component={AffiliateDashboardPage} />
      <Route path="/affiliate/leads" component={AffiliateLeadsPage} />
      <Route path="/affiliate/lokale" component={AffiliateLokalePage} />
      <Route path="/affiliate/provisionen" component={AffiliateProvisionenPage} />
      <Route path="/affiliate/konstantleiter" component={AffiliateKonstantleiterPage} />
      <Route path="/affiliate/marketing" component={AffiliateMarketingPage} />
      <Route path="/affiliate/zahlungen" component={AffiliateZahlungenPage} />
      <Route path="/affiliate/einstellungen" component={AffiliateEinstellungenPage} />
      {/* Fallback: redirect to affiliate dashboard */}
      <Route>
        {() => {
          window.location.href = '/affiliate';
          return null;
        }}
      </Route>
    </Switch>
  );
}

function App() {
  const [location] = useLocation();
  const isAffiliatePath = location.startsWith('/affiliate');
  
  // OAuth redirect detection: invalidate auth cache on ?oauth=success
  useOAuthRedirectHandler();
  
  // Globale Scroll-Restoration
  useScrollRestoration();
  
  // Affiliate Conversion Tracker — intercepts analytics events for ref-attributed users
  useAffiliateConversionTracker();
  
  // P0.3: Layout Measurement - Single Source of Truth für Sticky Offsets
  // Nur für Main-Layout relevant
  useLayoutMeasurement();
  
  // P0: Add has-navbar class to body for fixed navbar layout (nur Main-Layout)
  useEffect(() => {
    if (!isAffiliatePath) {
      document.body.classList.add('has-navbar');
    } else {
      document.body.classList.remove('has-navbar');
    }
    return () => {
      document.body.classList.remove('has-navbar');
    };
  }, [isAffiliatePath]);
  
  // AGENT D: Prefetch static data (cuisines + skills) on app mount for instant filter loading
  useEffect(() => {
    // Prefetch cuisines
    queryClient.prefetchQuery({
      queryKey: queryKeys.cuisines.list(),
      queryFn: async () => {
        const response = await fetch('/api/cuisines', { credentials: 'include' });
        if (!response.ok) throw new Error('Failed to prefetch cuisines');
        return response.json();
      },
      ...STATIC_DATA_CONFIG,
    });
    
    // Prefetch skills
    queryClient.prefetchQuery({
      queryKey: queryKeys.skills.list(),
      queryFn: async () => {
        const response = await fetch('/api/skills', { credentials: 'include' });
        if (!response.ok) throw new Error('Failed to prefetch skills');
        return response.json();
      },
      ...STATIC_DATA_CONFIG,
    });
  }, []);
  
  // P0.5: Global unhandled rejection handler (nur Dev) - fängt silent crashes
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
        console.error('Unhandled Promise Rejection (möglicherweise Google Maps blockiert):', event.reason);
        // Prevent default browser error logging
        event.preventDefault();
      };
      
      window.addEventListener('unhandledrejection', handleUnhandledRejection);
      
      return () => {
        window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      };
    }
    return () => {}; // No-op cleanup for production
  }, []);
  
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          {isAffiliatePath ? (
            // Affiliate Portal: Eigenes Layout (kein Navbar/Footer)
            <AffiliateRouter />
          ) : (
            // Main Site: Mit Navbar/Footer
            <div className="flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-grow">
                <MainRouter />
              </main>
              <Footer />
            </div>
          )}
          <Toaster />
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
